import { ApiPutBytes, ApiRequest } from './apiclient.js';

// Pieces of this size, rather than one request for the whole file. A five
// gigabyte upload that fails at the end has to continue, not start again, and
// the server only remembers what a finished piece left behind.
const ChunkBytes = 8 * 1000 * 1000;
// How many times one piece is retried before the upload gives up. The scene
// stays on the server either way, so giving up is not losing anything.
const PieceRetries = 3;

function Delay (milliseconds)
{
    return new Promise ((resolve) => setTimeout (resolve, milliseconds));
}

export class StorageClient
{
    constructor ()
    {
        // The last state the backend reported: how much is used, what the plan
        // allows. Kept so the interface can show it without asking again.
        this.state = null;
    }

    async GetState ()
    {
        let result = await ApiRequest ('GET', '/storage');
        this.state = result.storage;
        return this.state;
    }

    async ListScenes ()
    {
        let result = await ApiRequest ('GET', '/scenes');
        this.state = result.storage;
        return result.scenes;
    }

    async GetScene (sceneId)
    {
        let result = await ApiRequest ('GET', `/scenes/${encodeURIComponent (sceneId)}`);
        return result.scene;
    }

    async DeleteScene (sceneId)
    {
        await ApiRequest ('DELETE', `/scenes/${encodeURIComponent (sceneId)}`);
    }

    // Notes on the model. Sent whole every time, because that is how the
    // viewer holds them — there is no per-comment editing to support.
    async GetComments (sceneId)
    {
        let result = await ApiRequest ('GET', `/scenes/${encodeURIComponent (sceneId)}/comments`);
        return result.comments;
    }

    async SaveComments (sceneId, comments)
    {
        let result = await ApiRequest ('PUT', `/scenes/${encodeURIComponent (sceneId)}/comments`, {
            comments : comments
        });
        return result.comments;
    }

    async CreateShare (sceneId, settings)
    {
        let result = await ApiRequest ('POST', `/scenes/${encodeURIComponent (sceneId)}/share`,
            settings === undefined ? {} : settings);
        return result.share;
    }

    async RevokeShare (sceneId)
    {
        await ApiRequest ('DELETE', `/scenes/${encodeURIComponent (sceneId)}/share`);
    }

    // Opening a link needs no account at all — that is the whole point of it,
    // so this one asks for nothing and carries nothing.
    async GetSharedScene (token)
    {
        let result = await ApiRequest ('GET', `/share/${encodeURIComponent (token)}`);
        return result.scene;
    }

    // The password, typed once. What comes back is the model itself, and the
    // cookie left behind is what lets the viewer fetch its files.
    async UnlockShare (token, password)
    {
        let result = await ApiRequest ('POST', `/share/${encodeURIComponent (token)}/unlock`, {
            password : password
        });
        return result.scene;
    }

    // Reserve, send every file in pieces, then say it is done. The
    // reservation is what checks the quota, so a scene that will not fit is
    // refused before the first byte leaves the browser.
    //
    // Sending the same list of files again finds the upload that broke off
    // rather than starting a second one, and every file says how much of it
    // the server already has. That is all resuming is.
    async SaveScene (files, { name, mainFileName, onProgress, control = null, onResumed = null })
    {
        let declared = files.map ((file) => ({
            name : file.name,
            size : file.size,
            main : file.name === mainFileName
        }));
        let total = declared.reduce ((sum, file) => sum + file.size, 0);

        let created = await ApiRequest ('POST', '/scenes', { name : name, files : declared });
        let scene = created.scene;
        if (created.resumed === true && onResumed) {
            onResumed (scene);
        }

        try {
            // Files the server already has in full, counted before anything is
            // sent: the progress a person sees starts where it left off.
            let before = 0;
            for (let index = 0; index < scene.files.length; index++) {
                await this.SendFile (scene, index, files[index], before, total, onProgress, control);
                before += files[index].size;
            }
            let done = await ApiRequest ('POST', `/scenes/${scene.id}/complete`);
            this.state = done.storage;
            return done.scene;
        } catch (error) {
            if (error.code === 'aborted') {
                // Stopped on purpose: the space goes back straight away rather
                // than waiting for the sweep.
                try {
                    await this.DeleteScene (scene.id);
                } catch {
                    // Nothing to do about it here; the sweep will get to it.
                }
            }
            // Anything else leaves the upload where it is. The same files sent
            // again continue from that point, which is the whole reason the
            // half-finished scene is worth keeping.
            throw error;
        }
    }

    // One file, piece by piece, continuing from whatever the server already
    // has of it.
    async SendFile (scene, index, source, before, total, onProgress, control)
    {
        let target = scene.files[index];
        let offset = target.received || 0;
        let attempts = 0;

        while (offset < source.size) {
            let end = Math.min (source.size, offset + ChunkBytes);
            let from = offset;
            try {
                await ApiPutBytes (
                    `/scenes/${scene.id}/files/${target.id}`,
                    source.slice (from, end),
                    {
                        offset : from,
                        control : control,
                        onProgress : (loaded) => {
                            if (onProgress) {
                                onProgress (before + from + loaded, total);
                            }
                        }
                    }
                );
                offset = end;
                attempts = 0;
            } catch (error) {
                if (error.code === 'aborted' || (control !== null && control.IsCanceled ())) {
                    throw error;
                }
                attempts += 1;
                if (attempts > PieceRetries) {
                    throw error;
                }
                // A piece that broke halfway still left its bytes on the
                // server, so the next attempt has to start where they end —
                // the refusal says exactly where.
                if (error.code === 'offset_mismatch' && Number.isInteger (error.details.offset)) {
                    offset = error.details.offset;
                    continue;
                }
                if (error.code !== 'unavailable') {
                    throw error;
                }
                await Delay (500 * attempts);
            }
            if (onProgress) {
                onProgress (before + offset, total);
            }
        }
    }
}
